import { useState, useRef, useCallback, useMemo } from 'react'
import { motion } from 'framer-motion'
const MAX_TILT_DEG = 10
const TOUCH_DEADZONE_PX = 10
const TAP_SLOP_PX = 10
export default function Holo3DCard({
  imageUrl,
  fallbackImageUrl,
  alt,
  showAfterSplash = false,
  onActivate,
  maxWidthClass = 'sm:max-w-[320px]',
}) {
  const containerRef = useRef(null)
  const pressRef = useRef(null)
  const [tilt, setTilt] = useState({ x: 0.5, y: 0.5 })
  const [rotation, setRotation] = useState({ x: 0, y: 0 })
  const touchStartRef = useRef(null)
  const gestureModeRef = useRef('undetermined')
  // Primary photo first, then the fallback when the first one fails to load.
  const imageCandidates = useMemo(() => {
    const candidates = []
    const primary = imageUrl?.trim()
    if (primary) candidates.push(primary)
    const fallback = fallbackImageUrl?.trim()
    if (fallback && !candidates.includes(fallback)) candidates.push(fallback)
    return candidates
  }, [imageUrl, fallbackImageUrl])
  const [candidateIndex, setCandidateIndex] = useState(0)
  const imgSrc = imageCandidates[candidateIndex] ?? null
  const handleImageError = () =>
    setCandidateIndex((current) =>
      current < imageCandidates.length - 1 ? current + 1 : imageCandidates.length
    )

  const clamp = (v, min, max) => Math.max(min, Math.min(max, v))
  const updateGlareFromRotation = useCallback((rotX, rotY) => {
    const normX = clamp(rotX / MAX_TILT_DEG, -1, 1)
    const normY = clamp(rotY / MAX_TILT_DEG, -1, 1)
    const centerX = 0.5 - normY * 0.35
    const centerY = 0.5 - normX * 0.35
    setTilt({
      x: clamp(centerX, 0, 1),
      y: clamp(centerY, 0, 1),
    })
  }, [])
  const resetTilt = useCallback(() => {
    setRotation({ x: 0, y: 0 })
    setTilt({ x: 0.5, y: 0.5 })
  }, [])
  const handlePointerMove = useCallback(
    (e) => {
      if (e.pointerType !== 'mouse') return
      const el = containerRef.current
      if (!el) return
      const rect = el.getBoundingClientRect()
      const normX = ((e.clientX - rect.left) / rect.width) * 2 - 1
      const normY = ((e.clientY - rect.top) / rect.height) * 2 - 1
      const rotY = clamp(normX * MAX_TILT_DEG, -MAX_TILT_DEG, MAX_TILT_DEG)
      const rotX = clamp(-normY * MAX_TILT_DEG, -MAX_TILT_DEG, MAX_TILT_DEG)
      setRotation({ x: rotX, y: rotY })
      updateGlareFromRotation(rotX, rotY)
    },
    [updateGlareFromRotation]
  )
  const handlePointerLeave = useCallback(() => {
    resetTilt()
  }, [resetTilt])
  const handleTouchStart = useCallback((e) => {
    const t = e.touches[0]
    if (!t) return
    touchStartRef.current = { x: t.clientX, y: t.clientY }
    gestureModeRef.current = 'undetermined'
  }, [])
  const handleTouchMove = useCallback(
    (e) => {
      const t = e.touches[0]
      const start = touchStartRef.current
      const el = containerRef.current
      if (!t || !start || !el) return
      const dx = t.clientX - start.x
      const dy = t.clientY - start.y
      if (gestureModeRef.current === 'undetermined') {
        const distance = Math.hypot(dx, dy)
        if (distance < TOUCH_DEADZONE_PX) return
        gestureModeRef.current = Math.abs(dy) > Math.abs(dx) ? 'scroll' : 'tilt'
      }
      if (gestureModeRef.current === 'scroll') return
      const rect = el.getBoundingClientRect()
      const rotY = clamp((dx / rect.width) * MAX_TILT_DEG * 2, -MAX_TILT_DEG, MAX_TILT_DEG)
      const rotX = clamp((-dy / rect.height) * MAX_TILT_DEG * 2, -MAX_TILT_DEG, MAX_TILT_DEG)
      setRotation({ x: rotX, y: rotY })
      updateGlareFromRotation(rotX, rotY)
    },
    [updateGlareFromRotation]
  )
  const handleTouchEnd = useCallback(() => {
    touchStartRef.current = null
    gestureModeRef.current = 'undetermined'
    resetTilt()
  }, [resetTilt])
  const glareX = tilt.x * 100
  const glareY = tilt.y * 100
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{
        duration: 0.6,
        delay: showAfterSplash ? 0.1 : 0,
        ease: [0.16, 1, 0.3, 1],
      }}
      className={`relative w-full max-w-[max(140px,min(86vw,calc((100dvh_-_340px)_*_0.68)))] ${maxWidthClass} mx-auto select-none`}
    >
      <motion.div
        ref={containerRef}
        className="relative w-full cursor-pointer"
        role={onActivate ? 'button' : void 0}
        tabIndex={onActivate ? 0 : void 0}
        aria-label={onActivate ? `View ${alt} full screen` : void 0}
        onPointerDown={(e) => {
          pressRef.current = { x: e.clientX, y: e.clientY }
        }}
        onClick={(e) => {
          const start = pressRef.current
          pressRef.current = null
          if (start && Math.hypot(e.clientX - start.x, e.clientY - start.y) > TAP_SLOP_PX) return
          onActivate?.()
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            onActivate?.()
          }
        }}
        style={{
          transformPerspective: 900,
          rotateX: rotation.x,
          rotateY: rotation.y,
          touchAction: 'pan-y',
        }}
        transition={{ type: 'spring', stiffness: 240, damping: 22 }}
        onPointerMove={handlePointerMove}
        onPointerLeave={handlePointerLeave}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onTouchCancel={handleTouchEnd}
      >
        {/* NFT image defines the card size; the frame is baked into the image */}
        {imgSrc ? (
          <img
            src={imgSrc}
            alt={alt}
            className="block w-full h-auto"
            draggable={false}
            decoding="async"
            referrerPolicy="no-referrer"
            onError={handleImageError}
          />
        ) : (
          <div
            className="flex items-center justify-center bg-[#1a1b1e] text-[#72767d] text-xs text-center px-2"
            style={{ aspectRatio: '9 / 16' }}
            aria-hidden
          >
            Image unavailable
          </div>
        )}

        {/* Glare overlay reacting to movement (light reflection) */}
        <div
          className="absolute inset-0 z-20 pointer-events-none"
          style={{
            background: `
              radial-gradient(
                circle 50% at ${glareX}% ${glareY}%,
                rgba(255,255,255,0.18) 0%,
                rgba(255,255,255,0.06) 25%,
                rgba(255,255,255,0.02) 50%,
                transparent 100%
              ),
              radial-gradient(
                circle 40% at ${glareX}% ${glareY}%,
                rgba(255,255,255,0.08) 0%,
                transparent 80%
              )
            `,
          }}
        />
      </motion.div>
    </motion.div>
  )
}
